/**
 * Power Distribution draft/name/clone bindings for the descriptor engine (#997
 * Story 6.7, mirror of `largeUpsEngineBindings.ts`).
 *
 * The engine's generic factories (`descriptorCreateDraft` /
 * `descriptorEnsureNameForCreate` / `descriptorCloneForCreate`) don't match the
 * bespoke Power Distribution CREATE-flow semantics: bespoke drafts carry
 * `DEFAULT_POWER_DISTRIBUTION_CONFIGURATION` (status pre-filled to 'new'),
 * auto-names are serial hashes (`generateAutoPowerDistributionName`), and
 * duplicates take a fresh name while dropping id/metadata. Per the story ADR
 * (parity by reuse), each binding here delegates to the PROVEN bespoke util and
 * only converts shapes at the engine boundary (`PowerDistributionConfiguration` ↔
 * `DescriptorInstance` via the Story 6.7 converters).
 */

import {
  clonePowerDistributionForCreate,
  createDraftPowerDistributionConfiguration,
} from '../../../../components/powerdistribution/powerDistributionListUtils';
import {
  descriptorInstanceToPowerDistribution,
  powerDistributionToDescriptorInstance,
} from '../../../../services/descriptorEditSessionFlush';
import { generateAutoPowerDistributionName } from '../../../../utils/powerDistributionAutoName';
import { POWER_DISTRIBUTION_DESCRIPTOR } from '../../../sections/powerDistribution.descriptor';

import type { UpdatePowerDistributionConfigurationInput } from '../../../../types/powerDistribution';
import type {
  DescriptorInstance,
  DescriptorInstancePatch,
} from '../../../sections/descriptorEngineAdapter';

function patchToPowerDistributionInput(
  data: DescriptorInstancePatch,
): UpdatePowerDistributionConfigurationInput {
  const { instanceName, instanceStatus, ...fields } = data;
  return {
    ...fields,
    name: typeof instanceName === 'string' ? instanceName : undefined,
    status:
      instanceStatus as UpdatePowerDistributionConfigurationInput['status'],
  } as UpdatePowerDistributionConfigurationInput;
}

/** Bespoke draft factory: DEFAULT_POWER_DISTRIBUTION_CONFIGURATION + normalizeStatus. */
export function powerDistributionCreateDraft(
  data: DescriptorInstancePatch,
  displayOrder: number,
  tempId: number,
): DescriptorInstance {
  const unit = createDraftPowerDistributionConfiguration(
    patchToPowerDistributionInput(data),
    displayOrder,
    tempId,
  );
  return powerDistributionToDescriptorInstance(unit) as DescriptorInstance;
}

/** Bespoke auto-name: serial hash, mirrors ensurePowerDistributionNameForCreate. */
export function powerDistributionEnsureNameForCreate(
  data: DescriptorInstancePatch,
  tempId: number,
  takenNames: Iterable<string | undefined>,
): DescriptorInstancePatch {
  if (typeof data.instanceName === 'string' && data.instanceName.trim()) {
    return data;
  }
  return {
    ...data,
    instanceName: generateAutoPowerDistributionName(tempId, takenNames),
  };
}

/** Bespoke duplicate: fresh name, keeps status, drops id/metadata. */
export function powerDistributionCloneForCreate(
  source: DescriptorInstance,
  takenNames: Iterable<string | undefined>,
  tempId: number,
): Partial<DescriptorInstance> {
  const sourceUnit = descriptorInstanceToPowerDistribution(
    source,
    0,
    POWER_DISTRIBUTION_DESCRIPTOR,
  );
  const cloned = clonePowerDistributionForCreate(
    sourceUnit,
    takenNames,
    tempId,
  );
  return powerDistributionToDescriptorInstance({
    ...cloned,
    id: tempId,
  } as Parameters<typeof powerDistributionToDescriptorInstance>[0]) as Partial<DescriptorInstance>;
}
